import { VoucherRewardType } from '@diamond/shared/types';
import {
  useDateFilter,
  useSearchbox,
  useSelectParams,
} from '@diamond/sol-admin/hooks';
import { useMemo } from 'react';

import {
  VOUCHER_DATE_FILTER_CONFIG,
  VOUCHER_PAGE_SIZE,
  voucherListSelectParamsConfig,
} from '../constants';
import type { VoucherFilters, VoucherStatus } from '../types';
import { toUnixTimestamp } from '../utils/date-converter';

export const useVoucherFilters = (currentPage: number) => {
  // Search hook
  const searchHook = useSearchbox({ isDebounce: false, resetPage: false });

  // Select params hook
  const selectParams = useSelectParams<{
    rewardType: string;
    statusVoucher: string;
  }>(voucherListSelectParamsConfig, { resetPage: false });

  // Date filter hook
  const dateFilterHook = useDateFilter(VOUCHER_DATE_FILTER_CONFIG);

  // Convert dates to Unix timestamps
  const startDateUnix = toUnixTimestamp(dateFilterHook.apiDates.period_from);
  const endDateUnix = toUnixTimestamp(dateFilterHook.apiDates.period_to, true);

  // Build filter object
  const filters = useMemo(() => {
    const voucherFilters: VoucherFilters = {
      page: currentPage,
      size: VOUCHER_PAGE_SIZE,
      search_query: searchHook.params.query || undefined,
      mechanism:
        (selectParams.apiParams.rewardType as VoucherRewardType | '') || '',
      status:
        (selectParams.apiParams.statusVoucher as VoucherStatus | '') || '',
    };

    if (typeof startDateUnix === 'number') {
      voucherFilters.start_date = startDateUnix;
    }

    if (typeof endDateUnix === 'number') {
      voucherFilters.end_date = endDateUnix;
    }

    return voucherFilters;
  }, [
    currentPage,
    searchHook.params.query,
    selectParams.apiParams.rewardType,
    selectParams.apiParams.statusVoucher,
    startDateUnix,
    endDateUnix,
  ]);

  return {
    filters,
    searchHook,
    selectParams,
    dateFilterHook,
  };
};
